import { LottieRefCurrentProps } from 'lottie-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useActiveBrowser,
  useApproveCart,
  useCurrentUser,
  useGetCartQuery,
  useRejectCart,
} from 'src/hooks';

type Props = {
  cartId: string;
  setShowRequestApprovalModal: (show: boolean) => void;
};

export const useApprovalResponse = ({
  cartId,
  setShowRequestApprovalModal,
}: Props) => {
  const { user } = useCurrentUser();
  const lottieRefHeader = useRef<LottieRefCurrentProps | null>(null);
  const lottieRefFooter = useRef<LottieRefCurrentProps | null>(null);

  const activeBrowser = useActiveBrowser();
  const navigate = useNavigate();
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  const cartQuery = useGetCartQuery({ cartId });

  const { mutateAsync: approveCart } = useApproveCart({
    cartId,
  });

  const { mutateAsync: rejectCart } = useRejectCart();

  // can't be here unless approvalLayer exists, default to 1 if it's missing for some reason. API will
  // error out if it's the incorrect layer.
  const currentLayer =
    cartQuery.data?.approvalDetails?.at(-1)?.approvalLayer || 1;

  const handleApproveOrReject = async (
    response: 'approve' | 'reject',
    rejectNotes?: string,
  ) => {
    lottieRefHeader.current?.playSegments([0, 30], true);
    lottieRefFooter.current?.playSegments([0, 30], true);

    // Safari does not execute redirects in intervals or timeouts
    if (activeBrowser !== 'Safari') {
      // wait for the lottie animation to run
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    if (response === 'approve') {
      // if multi-layer approvals is enabled, we want to show the request approval modal
      // before we send the 'approved' answer. This will prevent the user from submitting the cart
      // and then not sending it for another layer of approval if necessary.
      if (user?.subscription.orgSettings?.multiLayerApproval) {
        setIsDirty(true);
        isDirtyRef.current = true;
        setShowRequestApprovalModal(true);
      } else {
        await approveCart({
          layer: currentLayer,
          emailOriginalRequester: true, // multi-layer approvals disabled, so we notify the original requester that the cart has been approved
        });
        navigate(`/review/${cartId}/approved`);
      }
    } else if (response === 'reject') {
      await rejectCart({
        cartId,
        rejectNotes: rejectNotes || `Rejected by ${user?.name || user?.email}`,
        layer: currentLayer,
      });
      navigate(`/review/${cartId}/rejected`);
    }

    // lottieRefHeader.current?.setDirection(-1);
    // lottieRefFooter.current?.setDirection(-1);

    lottieRefHeader.current?.playSegments([30, 0], true);
    lottieRefFooter.current?.playSegments([30, 0], true);

    // await new Promise((resolve) => setTimeout(resolve, 1000));

    // lottieRefHeader.current?.setDirection(1);
    // lottieRefFooter.current?.setDirection(1);
  };

  /**
   * Resets the isDirty state when the user completes the approval flow
   * This should be called after the cart is submitted for approval or when the user chooses not to require approval
   */
  const resetIsDirty = () => {
    setIsDirty(false);
    isDirtyRef.current = false;
  };

  return {
    handleApproveOrReject,
    lottieRefHeader,
    lottieRefFooter,
    isDirty,
    resetIsDirty,
  };
};
